import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Shield, Loader2 } from "lucide-react";

interface ReportDialogProps {
  adId?: Id<"ads">;
  userId?: Id<"users">;
  trigger?: React.ReactNode;
}

const REPORT_TYPES = [
  { value: "human_trafficking", label: "Suspected Human Trafficking", icon: "🚨" },
  { value: "child_exploitation", label: "Child Exploitation/Abuse", icon: "⚠️" },
  { value: "illegal_services", label: "Illegal Services", icon: "🚫" },
  { value: "fraud", label: "Fraud/Scam", icon: "💳" },
  { value: "harassment", label: "Harassment/Threats", icon: "😠" },
  { value: "other", label: "Other Safety Concern", icon: "❓" },
] as const;

export function ReportDialog({ adId, userId, trigger }: ReportDialogProps) {
  const [open, setOpen] = useState(false);
  const [reportType, setReportType] = useState<string>("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitReport = useMutation(api.safety.submitReport);

  const handleSubmit = async () => {
    if (!reportType) {
      toast.error("Please select a report type");
      return;
    }
    if (!description.trim()) {
      toast.error("Please provide a description");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitReport({
        reportedAdId: adId,
        reportedUserId: userId,
        reportType: reportType as any,
        description: description.trim(),
      });

      toast.success("Report submitted successfully. Thank you for helping keep our community safe.");
      
      if (result.priority === "critical") {
        toast.info("This report has been marked as high priority and will be reviewed immediately.", {
          duration: 5000,
        });
      }

      setOpen(false);
      setReportType("");
      setDescription("");
    } catch (error) {
      toast.error("Failed to submit report. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Report
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Report Suspicious Content
          </DialogTitle>
          <DialogDescription>
            Your report helps keep our community safe. Reports of trafficking or child exploitation 
            are treated with the highest priority and may be shared with law enforcement.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="report-type">What type of concern are you reporting?</Label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger>
                <SelectValue placeholder="Select a report type" />
              </SelectTrigger>
              <SelectContent>
                {REPORT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <span className="flex items-center gap-2">
                      <span>{type.icon}</span>
                      <span>{type.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Please describe your concern</Label>
            <Textarea
              id="description"
              placeholder="Provide as much detail as possible about why you believe this content is suspicious or harmful..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Include specific details like suspicious phrases, unusual requests, or anything 
              that made you concerned.
            </p>
          </div>

          {(reportType === "human_trafficking" || reportType === "child_exploitation") && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <p className="text-sm text-destructive font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Critical Report
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Reports of this nature are taken very seriously. If you believe someone is in 
                immediate danger, please also contact local law enforcement or the National 
                Human Trafficking Hotline: <strong>1-888-373-7888</strong>
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Report"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
